import { useMemo, useEffect, useCallback } from 'react';
import { RegistryProvider } from './registry/Provider';
import { createRegistryClient } from './registry/client';
import { createMockRegistryClient } from './registry/mockClient';
import type { RegistryClient } from './registry/client';
import { ThemeProvider } from './ui/theme';
import { Header } from './ui/layout';
import { RoomTabsView } from './ui/views/RoomTabsView';
import { ViewModeProvider } from './context/ViewModeContext';
import { HAConnectionProvider } from './context/HAConnectionContext';
import { loadSettings } from './settings/loader';
import { useAdaptiveHAConnection } from './hooks/useAdaptiveHAConnection';
import type { ActiveMode } from './hooks/useAdaptiveHAConnection';
import { useHARegistryQuery } from './hooks/useHARegistryQuery';
import { useHAStates } from './hooks/useHAStates';
import { useHAConfig } from './hooks/useHAConfig';
import type { HAConnectionState } from './ha/api';
import './index.css';

interface AppShellProps {
  connectionState: HAConnectionState;
  disabledReason?: string;
  locationName?: string;
  activeMode?: ActiveMode;
  onRetry?: () => void;
  onLogout?: () => void;
}

function AppShell({
  connectionState,
  disabledReason,
  locationName,
  activeMode,
  onRetry,
  onLogout,
}: AppShellProps): JSX.Element {
  const isMock = disabledReason === 'mockRegistry';
  const authInvalid = connectionState === 'auth_invalid';
  const disconnected =
    !isMock &&
    (connectionState === 'failed' ||
      connectionState === 'disconnected' ||
      connectionState === 'closed');
  const loading =
    !isMock &&
    (connectionState === 'idle' ||
      connectionState === 'connecting' ||
      connectionState === 'reconnecting');

  return (
    <div className="rsc-app min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white">
      <Header
        connectionState={connectionState}
        disabledReason={disabledReason}
        locationName={locationName}
        activeMode={activeMode}
        onLogout={onLogout}
      />
      <RoomTabsView loading={loading} disconnected={disconnected} authInvalid={authInvalid} onRetry={onRetry} />
    </div>
  );
}

function App(): JSX.Element {
  const settings = loadSettings();
  const { connection, state: connectionState, disabledReason, activeMode, logout } = useAdaptiveHAConnection();

  // Warm react-query caches for registry and live states.
  useHARegistryQuery(connection);
  useHAStates(connection);
  const haConfig = useHAConfig(connection);
  const locationName = haConfig?.location_name;

  const onRetry = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    document.title = locationName ? locationName + ' - RSC hub' : 'RSC hub';
  }, [locationName]);

  // Einmalig: alte Service-Worker + Caches loeschen, damit installierte PWAs
  // beim naechsten Start das aktuelle Bundle laden.
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((rs) => {
        rs.forEach((r) => r.unregister());
      });
    }
    if ('caches' in window) {
      caches.keys().then((keys) => {
        keys.forEach((k) => caches.delete(k));
      });
    }
  }, []);

  const client = useMemo<RegistryClient | undefined>(() => {
    if (settings.mockRegistry) return createMockRegistryClient();
    if (connection) return createRegistryClient(connection);
    return undefined;
  }, [settings.mockRegistry, connection]);

  return (
    <ThemeProvider themeConfig={settings.theme}>
      <ViewModeProvider>
        <RegistryProvider client={client}>
          <HAConnectionProvider connection={connection}>
            <AppShell
              connectionState={connectionState}
              disabledReason={disabledReason}
              locationName={locationName}
              activeMode={activeMode}
              onRetry={onRetry}
              onLogout={logout}
            />
          </HAConnectionProvider>
        </RegistryProvider>
      </ViewModeProvider>
    </ThemeProvider>
  );
}

export default App;
